import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Button, Menu, MenuItem, IconButton, Box, Typography, Drawer
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import PublicIcon from '@mui/icons-material/Public';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import logo from '../logo.png';

const CommonAppBar = ({ 
  title = "Ministry of Education",
  showHomeButton = true,
  showContactButton = true,
  showLoginButton = true,
  customButtons = []
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();

  // Check authentication status
  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');
      
      console.log('CommonAppBar - Checking auth status:', { token, userData, isLoggedIn });
      
      if (token && token !== 'null' && token !== 'undefined') {
        setIsLoggedIn(true);
        if (userData) {
          try {
            const parsedData = JSON.parse(userData);
            setUserEmail(parsedData.email || '');
          } catch (error) {
            console.error('Error parsing user data:', error);
          }
        }
      } else {
        setIsLoggedIn(false);
        setUserEmail('');
      }
    };

    // Check on mount
    checkAuthStatus();

    // Listen for storage changes (when user logs in/out in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'token' || e.key === 'userData') {
        checkAuthStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check periodically in case of race conditions
    const interval = setInterval(checkAuthStatus, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = () => {
    // Close mobile menu if open
    setMobileMenuOpen(false);
    
    // Navigate to logout route which will handle the cleanup
    navigate('/logout');
  };

  const handleContactClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleContactClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      <style>
        {`
          /* Force AppBar to break out of containers */
          .MuiAppBar-root {
            width: 100vw !important;
            max-width: 100vw !important;
            left: 50% !important;
            right: 50% !important;
            margin-left: -50vw !important;
            margin-right: -50vw !important;
            position: relative !important;
            z-index: 1100 !important;
          }
          
          /* Ensure no parent container constraints */
          .MuiAppBar-root * {
            box-sizing: border-box !important;
          }
          
          /* Mobile specific overrides */
          @media (max-width: 600px) {
            .MuiAppBar-root {
              left: 0 !important;
              right: 0 !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
              width: 100vw !important;
              max-width: 100vw !important;
            }
          }
        `}
      </style>
      <AppBar 
        position="static" 
        sx={{ 
          width: '100vw',
          maxWidth: '100vw',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          position: 'relative',
          backgroundColor: 'rgb(18,82,166)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1100
        }}
      >
        <Toolbar sx={{ 
          width: '100%',
          px: { xs: 1, md: 2 },
          minHeight: { xs: '56px', md: '64px' },
          margin: 0,
          padding: 0
        }}>
          {/* Logo and Title - Always visible */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            flexGrow: 1
          }}>
            <img src={logo} alt="Ministry of Education Logo" style={{ 
              maxHeight: '50px', 
              marginRight: '10px' 
            }} />
            <Typography variant="h6" sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', md: '1.25rem' },
              color: 'white'
            }}>
              {title}
            </Typography>
          </Box>

          {/* Desktop Navigation - Hidden on mobile */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' },
            gap: 1,
            alignItems: 'center'
          }}>
            {showHomeButton && (
              <Button 
                color="inherit" 
                onClick={() => navigate('/')}
                sx={{ fontSize: '0.875rem' }}
              >
                Home
              </Button>
            )}
            
            {showContactButton && (
              <Button 
                color="inherit" 
                onClick={handleContactClick}
                sx={{ fontSize: '0.875rem' }}
              >
                Contact
              </Button>
            )}
            
            {showContactButton && (
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleContactClose}
                PaperProps={{
                  sx: { 
                    maxWidth: { xs: 'calc(100vw - 32px)', md: 'auto' },
                    maxHeight: { xs: 'calc(100vh - 32px)', md: 'auto' }
                  }
                }}
              >
                <MenuItem onClick={handleContactClose}>
                  <a href="https://t.me/ngatsupportofficial" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>Telegram</a>
                </MenuItem>
              </Menu>
            )}
            
            {isLoggedIn ? (
              <Button 
                color="inherit" 
                onClick={handleLogout}
                sx={{ fontSize: '0.875rem' }}
                startIcon={<LogoutIcon />}
              >
                Logout
              </Button>
            ) : (
              showLoginButton && (
                <Button 
                  color="inherit" 
                  onClick={() => navigate('/login')}
                  sx={{ fontSize: '0.875rem' }}
                  startIcon={<AccountCircleIcon />}
                >
                  Login
                </Button>
              )
            )}
            
            {/* Custom buttons */}
            {customButtons.map((button, index) => (
              <Button
                key={index}
                color="inherit"
                onClick={button.onClick}
                sx={{ fontSize: '0.875rem' }}
              >
                {button.label}
              </Button>
            ))}
          </Box>

          {/* Mobile Hamburger Menu - Only visible on mobile */}
          <Box sx={{ 
            display: { xs: 'flex', md: 'none' },
            mr: { xs: -1, md: 0 }
          }}>
            <IconButton
              color="inherit"
              onClick={handleMobileMenuOpen}
              edge="end"
              size="large"
              sx={{ 
                mr: { xs: 0.5, md: 0 }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>

        {/* Mobile Menu Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={handleMobileMenuClose}
          PaperProps={{
            sx: {
              width: '280px',
              backgroundColor: 'rgb(18,82,166)',
              color: 'white',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }
          }}
          sx={{
            '& .MuiDrawer-paper': {
              border: 'none',
              borderRadius: '0'
            }
          }}
        >
          <Box sx={{ 
            p: 3,
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 4,
              pb: 2,
              borderBottom: '1px solid rgba(255,255,255,0.2)'
            }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 600,
                color: 'white'
              }}>
                Menu
              </Typography>
              <IconButton 
                color="inherit" 
                onClick={handleMobileMenuClose}
                size="large"
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            {/* Menu Items */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 1,
              flex: 1
            }}>
              {showHomeButton && (
                <Button 
                  color="inherit" 
                  onClick={() => { navigate('/'); handleMobileMenuClose(); }}
                  fullWidth
                  startIcon={<HomeIcon />}
                  sx={{ 
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Home
                  </Typography>
                </Button>
              )}
              
              {showContactButton && (
                <Button 
                  color="inherit" 
                  onClick={() => { handleContactClick(); handleMobileMenuClose(); }}
                  fullWidth
                  startIcon={<PublicIcon />}
                  sx={{ 
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Contact
                  </Typography>
                </Button>
              )}
              
              {isLoggedIn ? (
                <Button 
                  color="inherit" 
                  onClick={() => { handleLogout(); handleMobileMenuClose(); }}
                  fullWidth
                  startIcon={<LogoutIcon />}
                  sx={{ 
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Logout
                  </Typography>
                </Button>
              ) : (
                showLoginButton && (
                  <Button 
                    color="inherit" 
                    onClick={() => { navigate('/login'); handleMobileMenuClose(); }}
                    fullWidth
                    startIcon={<AccountCircleIcon />}
                    sx={{ 
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 2,
                      px: 3,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Login
                    </Typography>
                  </Button>
                )
              )}
              
              {/* Custom buttons */}
              {customButtons.map((button, index) => (
                <Button
                  key={index}
                  color="inherit"
                  onClick={() => { button.onClick(); handleMobileMenuClose(); }}
                  fullWidth
                  startIcon={button.icon}
                  sx={{ 
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {button.label}
                  </Typography>
                </Button>
              ))}
            </Box>
            
            {/* Footer */}
            <Box sx={{ 
              mt: 'auto',
              pt: 3,
              borderTop: '1px solid rgba(255,255,255,0.2)',
              textAlign: 'center'
            }}>
              <Typography variant="caption" sx={{ 
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.75rem'
              }}>
                NGAT Registration Portal
              </Typography>
            </Box>
          </Box>
        </Drawer>
      </AppBar>
    </>
  );
};

export default CommonAppBar;
